import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, RefreshCw, X } from "lucide-react";
import { xeroApi } from "../../backend/api/xero";
import { apiUrl } from "../../backend/api/http";
import type { Integration, SyncedInvoice, SyncRun } from "../../backend/api/xero";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  StatusBadge,
  formatDateTime,
  formatDuration,
  triggeredBy,
} from "./shared";

const money = (value?: string | null) => {
  const amount = Number(value ?? 0);
  return Number.isFinite(amount)
    ? amount.toLocaleString("en-AU", { style: "currency", currency: "AUD" })
    : "$0.00";
};

/** The same wording Xero's own screens use, so the two never disagree. */
const invoiceStatusLabel = (invoice: SyncedInvoice) => {
  const base = (() => {
    if (invoice.status !== "issued") {
      return invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1);
    }
    const due = Number(invoice.amount_due ?? 0);
    switch ((invoice.xero_status || "").toUpperCase()) {
      case "DRAFT":
        return "Draft";
      case "SUBMITTED":
        return "Awaiting Approval";
      case "AUTHORISED":
        if (due <= 0) return "Paid";
        return invoice.due_date && new Date(invoice.due_date) < new Date()
          ? "Overdue"
          : "Awaiting Payment";
      case "PAID":
        return "Paid";
      case "VOIDED":
        return "Voided";
      case "DELETED":
        return "Deleted";
      default:
        return "Issued";
    }
  })();

  return invoice.stranded_costs > 0 || invoice.unrecorded_lines > 0
    ? `${base} - Issues Identified`
    : base;
};

const invoiceStatusColour = (invoice: SyncedInvoice) => {
  if (invoice.stranded_costs > 0 || invoice.unrecorded_lines > 0)
    return "text-red-600";
  if (invoice.status === "voided") return "text-amber-600";
  if (invoiceStatusLabel(invoice) === "Overdue") return "text-red-600";
  if (Number(invoice.amount_due ?? 0) === 0 && invoice.status === "issued")
    return "text-emerald-600";
  return "text-foreground";
};

export default function IntegrationDetail() {
  const { integrationId = "" } = useParams();
  const navigate = useNavigate();

  const [integration, setIntegration] = useState<Integration | null>(null);
  const [runs, setRuns] = useState<SyncRun[]>([]);
  const [invoices, setInvoices] = useState<SyncedInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(
    async (quiet = false) => {
      if (!quiet) setLoading(true);
      const [integrationsResult, runsResult, recordsResult] = await Promise.all([
        xeroApi.listIntegrations(),
        xeroApi.listRuns(integrationId),
        xeroApi.listInvoices(),
      ]);
      if (!quiet) setLoading(false);

      if (integrationsResult.error) {
        setError(integrationsResult.error.message || "Could not load this integration.");
        return;
      }

      setError("");
      const found =
        (integrationsResult.data || []).find((entry) => entry.id === integrationId) || null;
      setIntegration(found);
      setRuns(runsResult.data || []);
      setInvoices(recordsResult.data || []);
    },
    [integrationId],
  );

  useEffect(() => {
    load();
  }, [load]);

  // Queued and running work changes without anyone asking, so the page follows
  // it. Polling stops the moment nothing is in flight, so an idle screen is not
  // sitting on a timer.
  const inFlight = runs.some((run) => run.status === "queued" || run.status === "running");

  useEffect(() => {
    if (!inFlight) return undefined;
    const timer = window.setInterval(() => load(true), 2000);
    return () => window.clearInterval(timer);
  }, [inFlight, load]);

  // Asking for a sync returns as soon as it is queued. The new row appears in
  // the history straight away, so pressing this five times leaves five visible
  // rows rather than five requests waiting on each other.
  const sync = async () => {
    setSyncing(true);
    setError("");
    const { error: syncError } = await xeroApi.runIntegration(integrationId);
    setSyncing(false);

    if (syncError) {
      setError(syncError.message || "Could not queue the sync.");
    }
    await load(true);
  };

  const cancel = async (runId: string) => {
    const { error: cancelError } = await xeroApi.cancelRun(runId);
    if (cancelError) {
      setError(cancelError.message || "Could not cancel that run.");
    }
    await load(true);
  };

  return (
    <main className="mx-auto flex max-w-[1500px] flex-col gap-5 p-4 sm:p-6">
      <Link
        to="/integrations/xero"
        className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Xero
      </Link>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{integration?.name || "Sync"}</h1>
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={sync}
          disabled={syncing || !integration?.connected}
          aria-label="Refresh from Xero"
          title="Refresh from Xero"
        >
          <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="runs">
        <TabsList>
          <TabsTrigger value="runs">Run history</TabsTrigger>
          <TabsTrigger value="records">Invoices</TabsTrigger>
        </TabsList>

        <TabsContent value="runs">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Run history</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Started</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Triggered by</TableHead>
                      <TableHead className="text-right">Added</TableHead>
                      <TableHead className="text-right">Updated</TableHead>
                      <TableHead className="text-right">Unchanged</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Log</TableHead>
                      <TableHead className="w-[1%]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={9} className="h-24 text-center text-muted-foreground">
                          Loading runs...
                        </TableCell>
                      </TableRow>
                    ) : runs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="h-24 text-center text-muted-foreground">
                          No runs yet. Use the refresh button to start one.
                        </TableCell>
                      </TableRow>
                    ) : (
                      runs.map((run) => (
                        <TableRow key={run.id}>
                          <TableCell className="whitespace-nowrap font-medium">
                            {formatDateTime(run.started_at)}
                          </TableCell>
                          <TableCell>{formatDuration(run.duration_ms)}</TableCell>
                          <TableCell className="whitespace-nowrap">{triggeredBy(run)}</TableCell>
                          <TableCell className="text-right">{run.created_count}</TableCell>
                          <TableCell className="text-right">{run.updated_count}</TableCell>
                          <TableCell className="text-right">{run.unchanged_count}</TableCell>
                          <TableCell>
                            <StatusBadge status={run.status} />
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" asChild>
                              <Link to={`/integrations/xero/syncs/${integrationId}/runs/${run.id}`}>
                                View log
                              </Link>
                            </Button>
                          </TableCell>
                          <TableCell className="text-right">
                            {(run.status === "queued" || run.status === "running") && (
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label="Cancel this run"
                                title={
                                  run.cancel_requested_at
                                    ? "Cancelling at the next checkpoint"
                                    : "Cancel this run"
                                }
                                disabled={Boolean(run.cancel_requested_at)}
                                onClick={() => cancel(run.id)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="records">
          <Card>
              <CardHeader>
                <CardTitle className="text-base">Invoices</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Invoice</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Project</TableHead>
                        <TableHead>Due</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">Outstanding</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">PDF</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoices.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={8}
                            className="h-24 text-center text-muted-foreground"
                          >
                            No invoices raised yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        invoices.map((invoice) => (
                          <TableRow
                            key={invoice.id}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => navigate(`/projects/${invoice.project_id}`)}
                            title="Open the project this invoice belongs to"
                          >
                            <TableCell className="font-medium">
                              {invoice.xero_invoice_number || `Draft ${invoice.id}`}
                            </TableCell>
                            <TableCell>{invoice.contact_name}</TableCell>
                            <TableCell>#{invoice.project_id}</TableCell>
                            <TableCell className="whitespace-nowrap">
                              {invoice.due_date
                                ? new Date(invoice.due_date).toLocaleDateString("en-AU")
                                : "-"}
                            </TableCell>
                            <TableCell className="text-right">
                              {money(invoice.total)}
                            </TableCell>
                            <TableCell className="text-right">
                              {money(invoice.amount_due)}
                            </TableCell>
                            <TableCell
                              className={`whitespace-nowrap ${invoiceStatusColour(invoice)}`}
                            >
                              {invoiceStatusLabel(invoice)}
                            </TableCell>
                            <TableCell className="text-right">
                              {invoice.status === "issued" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  asChild
                                  // Stops the row click stealing the link.
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  <a
                                    href={`${apiUrl(`/api/invoices/${invoice.id}/pdf`)}`}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <ExternalLink className="h-4 w-4" />
                                    PDF
                                  </a>
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </main>
  );
}
